import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  People,
  SwapHoriz,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { getAuthToken } from '../../lib/authToken';

const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
});

const MetricCard = ({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
}) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        {icon}
      </Box>
      <Typography variant="h4">{value}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const GrowthAnalyticsPage: React.FC = () => {
  const [days, setDays] = useState(90);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['growth-analytics', days],
    queryFn: async () => {
      const res = await fetch(`/api/admin/super/growth?days=${days}`, {
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load growth metrics');
      return json.data;
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    console.error('Growth analytics error:', error);
  }

  const m = data?.metrics || {
    mrr: 0,
    arr: 0,
    clv: 0,
    demoToPaidPercent: 0,
    planChurnPercent: 0,
  };
  const t = data?.totals || { paid: 0, demo: 0, cancelledInWindow: 0 };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <ListLoadErrorAlert
          resourceLabel="growth metrics"
          message="Growth metrics couldn’t be loaded. Platform super-admin access is required (`PLATFORM_ADMIN_EMAILS` or `isPlatformAdmin`)."
          onRetry={() => refetch()}
        />
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Growth Analytics</Typography>
          <Typography variant="body2" color="text.secondary">
            SaaS metrics — MRR, CLV, churn, and demo→paid conversion (platform-wide).
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Window</InputLabel>
          <Select
            label="Window"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <MenuItem value={30}>30 days</MenuItem>
            <MenuItem value={90}>90 days</MenuItem>
            <MenuItem value={180}>180 days</MenuItem>
            <MenuItem value={365}>365 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="MRR"
            value={`$${Number(m.mrr).toLocaleString()}`}
            subtitle={`ARR $${Number(m.arr).toLocaleString()}`}
            icon={<AttachMoney color="primary" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg CLV"
            value={`$${Number(m.clv).toLocaleString()}`}
            subtitle="Lifetime from paid SaaS invoices"
            icon={<TrendingUp color="success" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Demo → Paid"
            value={`${m.demoToPaidPercent}%`}
            subtitle={`${t.paid} paid / ${t.demo} demo`}
            icon={<SwapHoriz color="info" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Plan churn"
            value={`${m.planChurnPercent}%`}
            subtitle={`${t.cancelledInWindow} cancelled in window`}
            icon={<People color="warning" />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Conversions by week
              </Typography>
              {(data?.conversionsByWeek || []).length === 0 ? (
                <Alert severity="info">No conversions in this window yet.</Alert>
              ) : (
                <Box sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data?.conversionsByWeek || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1976d2" name="Conversions" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plan distribution
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(data?.planDistribution || []).map((p: any) => (
                  <Chip key={p.plan} label={`${p.plan}: ${p.count}`} />
                ))}
                {(data?.planDistribution || []).length === 0 && (
                  <Typography color="text.secondary">No paid tenants yet.</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent conversions
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>When</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.recentConversions || []).slice(0, 8).map((c: any) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Typography variant="body2">{c.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.subdomain}
                          {c.sourcePlan ? ` ← ${c.sourcePlan}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>{c.plan}</TableCell>
                      <TableCell>
                        {c.convertedAt
                          ? format(new Date(c.convertedAt), 'MMM d')
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(data?.recentConversions || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography color="text.secondary">None yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GrowthAnalyticsPage;
